import React, { memo, useMemo, useCallback } from "react";
import { User, Crown } from "lucide-react";

const UserCard = memo(
  ({
    user,
    isSponsor = false,
    className = "",
    isEditMode = false,
    isSelected = false,
    onSelectionChange,
  }) => {
    const displayName = useMemo(() => {
      return user?.firstName && user?.lastName
        ? `${user?.firstName} ${user?.lastName}`
        : user?.email;
    }, [user?.firstName, user?.lastName, user?.email]);

    const sponsorBadge = useMemo(() => {
      return isSponsor ? (
        <span className="text-xs text-yellow-600">(Parrain)</span>
      ) : null;
    }, [isSponsor]);

    const businessSection = useMemo(() => {
      return user?.businessName ? (
        <div className="text-sm text-gray-500">{user?.businessName}</div>
      ) : null;
    }, [user?.businessName]);

    const customUrlSection = useMemo(() => {
      return user?.customUrl ? (
        <div className="text-xs text-blue-600 mt-1">URL: {user?.customUrl}</div>
      ) : null;
    }, [user?.customUrl]);

    const activationWarning = useMemo(() => {
      return user?.needToActiveAccount ? (
        <div className="text-xs text-orange-600 mt-1 font-medium">
          ⚠ Compte à activer
        </div>
      ) : null;
    }, [user?.needToActiveAccount]);

    const crownIcon = useMemo(() => {
      return isSponsor ? (
        <Crown className="absolute top-2 right-2 h-4 w-4 text-yellow-500" />
      ) : null;
    }, [isSponsor]);

    const handleSelectionChange = useCallback(() => {
      onSelectionChange?.(user, !isSelected);
    }, [onSelectionChange, user, isSelected]);

    const editModeCheckbox = useMemo(() => {
      if (!isEditMode) return null;

      return (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleSelectionChange}
          className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
        />
      );
    }, [isEditMode, isSelected, handleSelectionChange]);

    const cardClasses = useMemo(() => {
      const baseClasses = "border rounded p-3 bg-gray-50 relative";
      const editModeClasses = isEditMode
        ? "hover:bg-gray-100 transition-colors"
        : "";
      const selectedClasses = isSelected ? "ring-2 ring-red-200 bg-red-50" : "";

      return `${baseClasses} ${editModeClasses} ${selectedClasses} ${className}`;
    }, [isEditMode, isSelected, className]);

    return (
      <div className={cardClasses}>
        {crownIcon}
        <div className="font-medium flex items-center gap-2">
          {editModeCheckbox}
          <User className="h-4 w-4" />
          {displayName}
          {sponsorBadge}
        </div>
        <div className="text-sm text-gray-600">{user?.email}</div>
        {businessSection}
        {customUrlSection}
        {activationWarning}
      </div>
    );
  }
);

UserCard.displayName = "UserCard";

export default UserCard;
